import React, { useCallback, useEffect, useState } from 'react';
import Navigation from '../components/Navigation';
import api from '../utils/axios';
import './DatasetExplorer.css';

const number = value => value == null ? '—' : value.toLocaleString();

function DatasetCard({ dataset, admin, onChange, reportError }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(dataset.title);
  const [description, setDescription] = useState(dataset.description);
  const ready = dataset.available && dataset.published && dataset.status === 'ready';
  const update = async data => {
    setBusy(true);
    try {
      await api.patch(`datasets/${dataset.experiment_id}/`, data);
      setEditing(false);
      await onChange();
    } catch (error) {
      reportError(error.response?.data?.detail || 'Could not update this publication.');
    } finally { setBusy(false); }
  };
  const download = async () => {
    setBusy(true);
    try {
      const response = await api.get(`datasets/${dataset.experiment_id}/download/`, {
        responseType: 'blob', timeout: 300000,
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${dataset.experiment_code}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      let detail;
      try { detail = JSON.parse(await error.response.data.text()).detail; } catch (_) { /* network error */ }
      reportError(detail || 'Download failed. Please try again.');
    } finally { setBusy(false); }
  };
  return <article className="dataset-card">
    <div className="dataset-card-heading">
      <span className="dataset-code">{dataset.experiment_code}</span>
      <span className={`dataset-status ${ready ? 'ready' : ''}`}>
        {!dataset.available ? 'Version retired' : !dataset.published ? 'Unpublished' :
          dataset.status === 'ready' ? 'Ready to download' : dataset.status === 'failed' ? 'Export failed' : 'Preparing export'}
      </span>
    </div>
    <h2>{dataset.title}</h2>
    <p className="dataset-description">{dataset.description || 'No description has been added yet.'}</p>
    <dl className="dataset-stats">
      <div><dt>Samples / anchors</dt><dd>{number(dataset.sample_count)}</dd></div>
      <div><dt>Records</dt><dd>{number(dataset.record_count)}</dd></div>
      <div><dt>Result cells</dt><dd>{number(dataset.result_count)}</dd></div>
      <div><dt>Endpoints</dt><dd>{number(dataset.endpoint_count)}</dd></div>
    </dl>
    <p className="dataset-provenance">Local ACAS · Experiment ID {dataset.experiment_id}
      {dataset.exported_at && <> · Exported {new Date(dataset.exported_at).toLocaleString()}</>}
    </p>
    {dataset.feature_metadata_count === 0 && <p className="dataset-provenance">No linked feature metadata is available for this dataset.</p>}
    {dataset.error && <p role="alert">{dataset.error}</p>}
    <div className="dataset-actions">
      <button disabled={!ready || busy} onClick={download}>{busy ? 'Working…' : 'Download dataset ZIP'}</button>
      {admin && <>
        <label><input type="checkbox" checked={dataset.published} disabled={busy || (!dataset.available && !dataset.published)}
          onChange={event => update({ published: event.target.checked })} /> Published</label>
        <button className="secondary" disabled={busy} onClick={() => setEditing(!editing)}>Edit metadata</button>
        {dataset.published && dataset.available && <button className="secondary"
          disabled={busy || ['building', 'pending'].includes(dataset.status)}
          onClick={() => update({ rebuild: true })}>Rebuild export</button>}
      </>}
    </div>
    {admin && editing && <form className="dataset-edit" onSubmit={event => { event.preventDefault(); update({ title, description }); }}>
      <label>Dataset name<input required maxLength={10000} value={title} onChange={event => setTitle(event.target.value)} /></label>
      <label>Description<textarea maxLength={10000} rows={3} value={description} onChange={event => setDescription(event.target.value)} /></label>
      <button disabled={busy} type="submit">Save metadata</button>
    </form>}
  </article>;
}

export default function DatasetExplorer() {
  const [capabilities, setCapabilities] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [adminMode, setAdminMode] = useState(false);
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data: caps } = await api.get('datasets/capabilities/');
      setCapabilities(caps);
      if (caps.enabled) {
        const { data } = await api.get(`datasets/${adminMode && caps.admin ? '?admin=true' : ''}`);
        setDatasets(data.datasets);
      }
      setLoaded(true);
    } catch (_) { setError('Could not load the dataset catalog. Please refresh to try again.'); }
  }, [adminMode]);
  useEffect(() => {
    load();
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [load]);
  const filtered = datasets.filter(d => `${d.title} ${d.experiment_code} ${d.description}`.toLowerCase().includes(query.toLowerCase()));
  return <div className="dataset-page"><Navigation /><main className="dataset-explorer">
    <header><p className="dataset-eyebrow">SHADOWSTREAM · LOCAL ACAS</p><h1>Dataset Explorer</h1>
      <p>Browse published experiments and download data with its metadata.</p></header>
    {error && <div className="dataset-error" role="alert">{error}<button onClick={() => { setError(''); load(); }}>Retry</button></div>}
    {!loaded && !error && <p role="status">Loading datasets…</p>}
    {capabilities && !capabilities.enabled && <p>Dataset Explorer is currently disabled.</p>}
    {capabilities?.enabled && <>
      <div className="dataset-info">Each ZIP contains complete long-form results, a wide table, a column dictionary,
        sample and feature metadata, and export provenance. Wide tables preserve one row per analysis group;
        derived datasets contain statistical records and may reuse a sample anchor.</div>
      <div className="dataset-toolbar">
        <label>Find a dataset<input type="search" placeholder="Search name, description, or experiment code" value={query}
          onChange={event => setQuery(event.target.value)} /></label>
        {capabilities.admin && <label><input type="checkbox" checked={adminMode}
          onChange={event => { setDatasets([]); setAdminMode(event.target.checked); }} /> Manage publication</label>}
        <span>{filtered.length} datasets</span>
      </div>
      {adminMode && <p>Publish explicit experiment versions to all signed-in scientists. New experiment versions start unpublished.
        Retired versions cannot be downloaded. Unpublishing blocks subsequent downloads immediately.</p>}
      <div className="dataset-grid">{filtered.map(dataset => <DatasetCard key={dataset.experiment_id} dataset={dataset}
        admin={adminMode && capabilities.admin} onChange={load} reportError={setError} />)}</div>
      {loaded && !filtered.length && <p>No datasets match this view.</p>}
    </>}
  </main></div>;
}
